import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import { parseJsonUrl, stringifyToJsonUrl } from './JsonUrlUtil.js';

const DEFAULT_RECHERCHE_EXPERTE = {
  typeDossiers: [
    'Operation',
    'Collectif',
    'AireEtude',
    'Thematique',
    'Mobilier',
    'OeuvreObjet',
    'OeuvreArchitecture',
  ],
  blocs: [],
};

// JsonUrl côté Java ne supporte (pour le moment) pas ces caractères
// Ils ne devraient de toute manière pas apparaître dans le formulaire
const NOT_ALLOWED_CHARACTERS = ['{', '}', '#', '"', '<', '>', '^', '\\'];

const FormContext = createContext();

const extractSerializedRechercheFromUrl = () => {
  // On n'utilise pas UrlSearchParams car il décode la valeur du queryParam
  // Ce qu'on ne veut pas, JsonUrl a besoin de la valeur non décodée
  // (sinon les accents et autres caractères ne sont pas gérés)
  if (!window.location.search.startsWith('?')) {
    return null;
  }

  const [, serializedRechercheExperte] = window.location.search
    .substring(1)
    .split('&')
    .map((serializedQueryParam) => serializedQueryParam.split('='))
    .find(([property]) => property === 'q');
  return serializedRechercheExperte;
};

const retrieveRechercheExperteFormUrl = () => {
  const serializedRechercheExperte = extractSerializedRechercheFromUrl();
  if (serializedRechercheExperte) {
    return parseJsonUrl(serializedRechercheExperte);
  }
  return DEFAULT_RECHERCHE_EXPERTE;
};

export const FormProvider = ({ children }) => {
  const [rechercheExperte, setRechercheExperte] = useState(() =>
    retrieveRechercheExperteFormUrl(),
  );

  return (
    <FormContext.Provider value={{ rechercheExperte, setRechercheExperte }}>
      {children}
    </FormContext.Provider>
  );
};
FormProvider.propTypes = {
  children: PropTypes.node,
};

let lastUpdatedCritereId = null;
export const useRechercheExperte = () => {
  const { rechercheExperte, setRechercheExperte } = useContext(FormContext);

  useEffect(() => {
    // on ne compare pas directement avec la valeur dans l'URL
    // dans le cas où il y a une différence dans l'encodage
    // ce qui ajouterait une url dans l'historique du navigateur pour rien
    const currentSerializedRechercheExperte = stringifyToJsonUrl(
      retrieveRechercheExperteFormUrl(),
    );
    const newSerializedRechercheExperte = stringifyToJsonUrl(rechercheExperte);
    if (currentSerializedRechercheExperte !== newSerializedRechercheExperte) {
      window.history.pushState(
        { rechercheExperte },
        '',
        `${window.location.pathname}?q=${newSerializedRechercheExperte}`,
      );
    }
  }, [rechercheExperte]);

  window.onpopstate = function (e) {
    if (e.state && e.state.rechercheExperte) {
      setRechercheExperte(e.state.rechercheExperte);
    }
  };

  const rechercheExperteForUrl = () => {
    return stringifyToJsonUrl(rechercheExperte);
  };

  const addTypeDossier = (typeDossier) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      typeDossiers: [...currentRechercheExperte.typeDossiers, typeDossier],
    }));
  };

  const removeTypeDossier = (typeDossierToRemove) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...rechercheExperte,
      typeDossiers: currentRechercheExperte.typeDossiers.filter(
        (typeDossier) => typeDossier !== typeDossierToRemove,
      ),
    }));
  };

  const toggleTypeDossier = (typeDossier, enable) => {
    lastUpdatedCritereId = null;

    if (enable) {
      addTypeDossier(typeDossier);
    } else {
      removeTypeDossier(typeDossier);
    }
  };

  const addBloc = (thematiqueType) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      blocs: [
        ...currentRechercheExperte.blocs,
        {
          id: uuid(),
          thematiqueType,
          operateur: 'AND',
          criteres: [],
          position: currentRechercheExperte.blocs.length,
        },
      ],
    }));
  };

  const removeBloc = (blocId) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      blocs: currentRechercheExperte.blocs
        .filter((bloc) => bloc.id !== blocId)
        .map((bloc, index) => ({
          ...bloc,
          position: index,
        })),
    }));
  };

  const updateOperateur = (blocId, operateur) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      blocs: [
        ...currentRechercheExperte.blocs.map((bloc) => {
          if (bloc.id !== blocId) {
            return bloc;
          }
          return {
            ...bloc,
            operateur,
          };
        }),
      ],
    }));
  };

  const addCritere = (blocId, fieldId) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      blocs: [
        ...currentRechercheExperte.blocs.map((bloc) => {
          if (bloc.id !== blocId) {
            return bloc;
          }
          return {
            ...bloc,
            criteres: [
              ...bloc.criteres,
              {
                id: uuid(),
                champRecherche: fieldId,
                valeur: null,
              },
            ],
          };
        }),
      ],
    }));
  };

  const removeCritere = (blocId, critereId) => {
    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => ({
      ...currentRechercheExperte,
      blocs: [
        ...currentRechercheExperte.blocs.map((bloc) => {
          if (bloc.id !== blocId) {
            return bloc;
          }
          return {
            ...bloc,
            criteres: bloc.criteres.filter(
              (critere) => critere.id !== critereId,
            ),
          };
        }),
      ],
    }));
  };

  const checkValeur = (valeur) => {
    if (!valeur) {
      return null;
    }
    if (!(typeof valeur === 'string')) {
      return valeur;
    }

    return NOT_ALLOWED_CHARACTERS.reduce(
      (checkedValeur, notAllowedCharacter) =>
        checkedValeur.replaceAll(notAllowedCharacter, ''),
      valeur,
    );
  };

  const updateCritereValue = (blocId, critereId, critereParams) => {
    setRechercheExperte((currentRechercheExperte) => {
      const newRechercheExperte = {
        ...currentRechercheExperte,
        blocs: [
          ...currentRechercheExperte.blocs.map((bloc) => {
            if (bloc.id !== blocId) {
              return bloc;
            }
            return {
              ...bloc,
              criteres: bloc.criteres.map((critere) => {
                if (critere.id !== critereId) {
                  return critere;
                }
                return {
                  ...critere,
                  valeur: checkValeur(critereParams?.valeur) ?? undefined,
                  operateur: critereParams?.operateur ?? undefined,
                  label: critereParams?.label ?? undefined,
                  ancestorLabels: critereParams?.ancestorLabels ?? undefined,
                };
              }),
            };
          }),
        ],
      };

      if (lastUpdatedCritereId === critereId) {
        const newSerializedRechercheExperte =
          stringifyToJsonUrl(newRechercheExperte);
        window.history.replaceState(
          { rechercheExperte: newRechercheExperte },
          '',
          `${window.location.pathname}?q=${newSerializedRechercheExperte}`,
        );
      } else {
        // On ne modifie qu'une valeur on ne va donc pas rajouter une entrée dans l'historique de l'utilisateur
      }

      lastUpdatedCritereId = critereId;

      return newRechercheExperte;
    });
  };

  const moveBloc = (srcBloc, destBloc) => {
    if (srcBloc.id === destBloc.id) {
      return;
    }

    lastUpdatedCritereId = null;

    setRechercheExperte((currentRechercheExperte) => {
      const srcPosition = srcBloc.position;
      const destPosition = destBloc.position;
      if (srcPosition < 0 || destPosition < 0) {
        return currentRechercheExperte;
      }
      if (srcPosition < destPosition) {
        return {
          ...currentRechercheExperte,
          blocs: [
            ...currentRechercheExperte.blocs.slice(0, srcPosition),
            ...currentRechercheExperte.blocs.slice(
              srcPosition + 1,
              destPosition + 1,
            ),
            currentRechercheExperte.blocs[srcPosition],
            ...currentRechercheExperte.blocs.slice(destPosition + 1),
          ].map((bloc, index) => ({
            ...bloc,
            position: index,
          })),
        };
      }
      return {
        ...currentRechercheExperte,
        blocs: [
          ...currentRechercheExperte.blocs.slice(0, destPosition),
          currentRechercheExperte.blocs[srcPosition],
          ...currentRechercheExperte.blocs.slice(destPosition, srcPosition),
          ...currentRechercheExperte.blocs.slice(srcPosition + 1),
        ].map((bloc, index) => ({
          ...bloc,
          position: index,
        })),
      };
    });
  };

  const resetRechercheExperte = () => {
    lastUpdatedCritereId = null;

    setRechercheExperte(() => DEFAULT_RECHERCHE_EXPERTE);
  };

  return {
    rechercheExperte,
    rechercheExperteForUrl,
    toggleTypeDossier,
    addBloc,
    removeBloc,
    updateOperateur,
    addCritere,
    removeCritere,
    updateCritereValue,
    moveBloc,
    resetRechercheExperte,
  };
};
